import React from 'react';
import { Badge, Button, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { formatGuatemalanDateTime } from '../../Utils/dateFormat';
import './ExecutionRuns.scss';

const STATUS_CONFIG = {
  STARTED: { label: 'Started', variant: 'primary' },
  IN_PROGRESS: { label: 'In Progress', variant: 'primary' },
  INPROGRESS: { label: 'In Progress', variant: 'primary' },
  RUNNING: { label: 'In Progress', variant: 'primary' },
  SUCCEEDED: { label: 'Succeeded', variant: 'success' },
  SUCCESS: { label: 'Succeeded', variant: 'success' },
  COMPLETED: { label: 'Succeeded', variant: 'success' },
  FAILED: { label: 'Failed', variant: 'danger' },
  FAILURE: { label: 'Failed', variant: 'danger' },
  ERROR: { label: 'Failed', variant: 'danger' },
  WARNING: { label: 'Warning', variant: 'warning' },
  WARN: { label: 'Warning', variant: 'warning' }
};

export const getExecutionStatus = (status) => {
  const key = String(status || '').trim().replace(/[\s-]+/g, '_').toUpperCase();
  return STATUS_CONFIG[key] || { label: status || 'Unknown', variant: 'secondary' };
};

export const ExecutionStatusBadge = ({ status }) => {
  const config = getExecutionStatus(status);
  return <Badge bg={config.variant} text={config.variant === 'warning' ? 'dark' : undefined}>{config.label}</Badge>;
};

export const formatDuration = (durationSeconds) => {
  if (durationSeconds === null || durationSeconds === undefined || durationSeconds === '') return '-';
  const seconds = Math.max(0, Number(durationSeconds));
  if (!Number.isFinite(seconds)) return '-';
  if (seconds < 60) return `${seconds.toFixed(seconds % 1 ? 1 : 0)}s`;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return [hours ? `${hours}h` : null, minutes ? `${minutes}m` : null, remainingSeconds || (!hours && !minutes) ? `${remainingSeconds}s` : null]
    .filter(Boolean)
    .join(' ');
};

export const StepTimeline = ({ steps = [] }) => {
  const orderedSteps = React.useMemo(
    () => [...steps].sort((a, b) => (a.stepNumber ?? Number.MAX_SAFE_INTEGER) - (b.stepNumber ?? Number.MAX_SAFE_INTEGER)),
    [steps]
  );

  if (!orderedSteps.length) return <div className="execution-steps-empty">No steps recorded.</div>;

  return (
    <ol className="execution-step-timeline">
      {orderedSteps.map((step, index) => {
        const status = getExecutionStatus(step.status);
        return (
          <li className={`execution-step execution-step-${status.variant}`} key={step.id ?? `${step.stepNumber}-${index}`}>
            <div className="execution-step-marker">{step.stepNumber ?? index + 1}</div>
            <div className="execution-step-content">
              <div className="execution-step-heading">
                <ExecutionStatusBadge status={step.status} />
                <time>{step.timestamp ? formatGuatemalanDateTime(step.timestamp) : '-'}</time>
              </div>
              <div className="execution-step-message">{step.message || 'No message'}</div>
              {step.details && (
                <pre className="execution-step-details">
                  {typeof step.details === 'string' ? step.details : JSON.stringify(step.details, null, 2)}
                </pre>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export const ExecutionRunDetails = ({ execution }) => {
  if (!execution) return null;
  return (
    <div className="execution-run-modal-content">
      <div className="execution-detail-grid">
        <div><small>Process type</small><strong>{execution.processType || '-'}</strong></div>
        <div><small>Category</small><strong>{execution.category || '-'}</strong></div>
        <div><small>Status</small><span><ExecutionStatusBadge status={execution.status} /></span></div>
        <div><small>Duration</small><strong>{formatDuration(execution.durationSeconds)}</strong></div>
        <div><small>Started</small><strong>{execution.startedAt ? formatGuatemalanDateTime(execution.startedAt) : '-'}</strong></div>
        <div><small>Completed</small><strong>{execution.completedAt ? formatGuatemalanDateTime(execution.completedAt) : '-'}</strong></div>
      </div>
      <div className="execution-run-meta">
        <span><strong>Execution ID:</strong> {execution.executionId || '-'}</span>
        <span><strong>Scheduled job:</strong> {execution.scheduledJobId || '-'}</span>
      </div>
      <div className="execution-detail-message">
        <small>Message</small>
        <div>{execution.message || 'No message'}</div>
      </div>
      <h6 className="execution-timeline-title">Steps</h6>
      <StepTimeline steps={execution.steps} />
    </div>
  );
};

export const ExecutionRunDetailsModal = ({ execution, onHide }) => (
  <Modal show={!!execution} onHide={onHide} size="lg" centered scrollable aria-labelledby="execution-details-modal-title">
    <Modal.Header closeButton>
      <Modal.Title id="execution-details-modal-title">Execution details</Modal.Title>
    </Modal.Header>
    <Modal.Body><ExecutionRunDetails execution={execution} /></Modal.Body>
    <Modal.Footer><Button variant="primary" onClick={onHide}>Close</Button></Modal.Footer>
  </Modal>
);

export const ExecutionRun = ({ execution, compact = false, onViewDetails }) => (
  <article className={`execution-run ${compact ? 'execution-run-compact' : ''}`}>
    <div className='execution-run-card-title'>
      <span className="execution-run-process">
        <strong>{execution.processType || '-'}</strong>
        {!compact && <small>{execution.category || '-'}</small>}
      </span>
    </div>
    <div className="execution-run-card-content">
      <span className="execution-run-status">
        <ExecutionStatusBadge status={execution.status} />
      </span>
      <span className="execution-run-started">
        <small>Started</small>
        <span>{execution.startedAt ? formatGuatemalanDateTime(execution.startedAt) : '-'}</span>
      </span>
      <span className="execution-run-duration">
        <small>Duration</small>
        <span>{formatDuration(execution.durationSeconds)}</span>
      </span>
      <div className="execution-run-message">{execution.message || 'No message'}</div>
      <Button size="sm" variant="outline-secondary" onClick={() => onViewDetails(execution)}>
        <FontAwesomeIcon icon={faMagnifyingGlass} className="me-1" /> Details
      </Button>
    </div>
  </article>
);

const ExecutionRunList = ({ executions = [], compact = false, emptyMessage = 'No executions found.', onViewDetails }) => {
  const [selectedExecution, setSelectedExecution] = React.useState(null);
  const viewDetails = onViewDetails || setSelectedExecution;
  if (!executions.length) return <div className="execution-runs-empty">{emptyMessage}</div>;
  return (
    <>
      <div className="execution-run-list">
        {executions.map((execution) => (
          <ExecutionRun key={execution.executionId} execution={execution} compact={compact} onViewDetails={viewDetails} />
        ))}
      </div>
      {!onViewDetails && <ExecutionRunDetailsModal execution={selectedExecution} onHide={() => setSelectedExecution(null)} />}
    </>
  );
};

export default ExecutionRunList;
